import { siteConfig } from '../config/site'
import { ARCHETYPES } from '../data/archetypes'
import type { ArchetypeResult } from '../types/assessment'

/**
 * ============================================================
 * DOWNLOADABLE / SHAREABLE PROFILE CARD
 * ------------------------------------------------------------
 * The card is drawn on an offscreen <canvas> at 1080 x 1350
 * (the standard portrait ratio for phone wallpapers, Instagram,
 * and messaging previews) and exported as a PNG blob.
 *
 * No third-party image library is required.
 * The user's email address is never drawn on the card.
 * ============================================================
 */

const CARD_WIDTH = 1080
const CARD_HEIGHT = 1350
const PAD = 84

function hexAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, h / 2, w / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}

function drawTracked(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  tracking: number,
) {
  let cursor = x
  for (const char of text) {
    ctx.fillText(char, cursor, y)
    cursor += ctx.measureText(char).width + tracking
  }
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const words = text.split(' ')
  let line = ''
  let cursorY = y

  for (let i = 0; i < words.length; i += 1) {
    const test = line ? `${line} ${words[i]}` : words[i]
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cursorY)
      line = words[i]
      cursorY += lineHeight
    } else {
      line = test
    }
  }
  ctx.fillText(line, x, cursorY)
  return cursorY
}

/** Renders the complete eight-archetype profile card and returns it as a PNG blob. */
export async function renderProfileCard(profile: ArchetypeResult[]): Promise<Blob> {
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready
  }

  const primary = ARCHETYPES[profile[0].key]
  const theme = primary.theme

  const canvas = document.createElement('canvas')
  canvas.width = CARD_WIDTH
  canvas.height = CARD_HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas is not supported in this browser.')

  ctx.textBaseline = 'alphabetic'

  // Background
  ctx.fillStyle = theme.bg2
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT)

  // Hairline inner frame
  ctx.strokeStyle = hexAlpha(theme.text, 0.14)
  ctx.lineWidth = 2
  roundRect(ctx, 28, 28, CARD_WIDTH - 56, CARD_HEIGHT - 56, 22)
  ctx.stroke()

  // Eyebrow
  ctx.font = '500 22px "DM Sans", sans-serif'
  ctx.fillStyle = theme.accent
  drawTracked(ctx, 'RELATIONAL ARCHETYPES', PAD, 126, 4)

  ctx.fillStyle = hexAlpha(theme.accent, 0.4)
  ctx.fillRect(PAD, 150, 64, 2)

  // Title
  ctx.font = '700 68px "Playfair Display", Georgia, serif'
  ctx.fillStyle = theme.text
  ctx.fillText('Your Relational', PAD, 244)
  ctx.fillText('Archetype Profile', PAD, 320)

  // Primary archetype block
  ctx.font = '96px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", serif'
  ctx.fillText(primary.emoji, PAD, 470)

  ctx.font = '500 20px "DM Sans", sans-serif'
  ctx.fillStyle = hexAlpha(theme.text, 0.55)
  drawTracked(ctx, 'PRIMARY ARCHETYPE', PAD + 130, 410, 3.5)

  ctx.font = '700 58px "Playfair Display", Georgia, serif'
  ctx.fillStyle = theme.text
  ctx.fillText(`The ${primary.name}`, PAD + 128, 470)

  ctx.font = 'italic 27px "Playfair Display", Georgia, serif'
  ctx.fillStyle = hexAlpha(theme.text, 0.72)
  const coreY = wrapText(ctx, primary.core, PAD, 552, CARD_WIDTH - PAD * 2, 38)

  // Divider
  ctx.fillStyle = hexAlpha(theme.text, 0.16)
  ctx.fillRect(PAD, coreY + 44, CARD_WIDTH - PAD * 2, 1)

  // Eight archetype rows
  const rowsTop = coreY + 108
  const rowHeight = 64
  const barX = PAD + 300
  const barWidth = 460
  const pctX = CARD_WIDTH - PAD

  profile.forEach((entry, index) => {
    const archetype = ARCHETYPES[entry.key]
    const isPrimary = index === 0
    const y = rowsTop + index * rowHeight

    ctx.textAlign = 'left'
    ctx.font = '30px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", serif'
    ctx.fillStyle = theme.text
    ctx.globalAlpha = isPrimary ? 1 : 0.75
    ctx.fillText(archetype.emoji, PAD, y)

    ctx.font = isPrimary ? '500 30px "DM Sans", sans-serif' : '400 27px "DM Sans", sans-serif'
    ctx.fillStyle = isPrimary ? theme.accent : hexAlpha(theme.text, 0.85)
    ctx.globalAlpha = 1
    ctx.fillText(archetype.name, PAD + 52, y)

    // Bar
    const barY = y - 16
    ctx.fillStyle = hexAlpha(theme.text, 0.12)
    roundRect(ctx, barX, barY, barWidth, 12, 6)
    ctx.fill()

    const maxPct = profile[0].profilePct || 1
    const fillWidth = Math.max(6, (entry.profilePct / maxPct) * barWidth)
    ctx.fillStyle = isPrimary ? theme.accent : hexAlpha(theme.text, 0.42)
    roundRect(ctx, barX, barY, fillWidth, 12, 6)
    ctx.fill()

    // Percentage
    ctx.textAlign = 'right'
    ctx.font = isPrimary ? '500 30px "DM Sans", sans-serif' : '400 27px "DM Sans", sans-serif'
    ctx.fillStyle = isPrimary ? theme.accent : hexAlpha(theme.text, 0.85)
    ctx.fillText(`${entry.profilePct}%`, pctX, y)
  })

  ctx.textAlign = 'left'
  ctx.globalAlpha = 1

  // Footer
  const footerY = CARD_HEIGHT - 96
  ctx.fillStyle = hexAlpha(theme.text, 0.14)
  ctx.fillRect(PAD, footerY - 44, CARD_WIDTH - PAD * 2, 1)

  ctx.font = '22px "DM Sans", sans-serif'
  ctx.fillStyle = hexAlpha(theme.text, 0.6)
  ctx.fillText(siteConfig.brand.displayDomain, PAD, footerY)

  ctx.textAlign = 'right'
  ctx.font = 'italic 22px "Playfair Display", Georgia, serif'
  ctx.fillStyle = hexAlpha(theme.accent, 0.75)
  ctx.fillText(siteConfig.brand.practice, CARD_WIDTH - PAD, footerY)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Could not generate the image.'))
    }, 'image/png')
  })
}

export function profileCardFileName(profile: ArchetypeResult[]): string {
  return `relational-archetype-profile-${profile[0].key.toLowerCase()}.png`
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 2000)
}

/** True when the device supports sharing an image file through the OS share sheet. */
export function canShareFiles(): boolean {
  if (typeof navigator === 'undefined' || !navigator.canShare || !navigator.share) return false
  try {
    const probe = new File([new Blob()], 'probe.png', { type: 'image/png' })
    return navigator.canShare({ files: [probe] })
  } catch {
    return false
  }
}

export async function shareProfileCard(blob: Blob, fileName: string, primaryName: string) {
  const file = new File([blob], fileName, { type: 'image/png' })
  await navigator.share({
    files: [file],
    title: 'My Relational Archetype Profile',
    text: `My primary relational archetype is the ${primaryName}. Find yours at ${siteConfig.assessmentUrl}`,
  })
}
